import SearchParams from "./searchparams";
import { isWhiteLabelled } from "../../support/helpers";

// Kenya transactions must not touch a Flutterwave-branded host.
//
// `process.env.API_BASE_URL` is substituted by rollup at BUILD time, so every
// endpoint in the runtime is a hardcoded string by the time it ships, and one
// bundle serves every region. The host therefore cannot be chosen at build time —
// it has to be swapped per session. Rather than have ~17 call sites each resolve
// their own base URL, this rewrites the host at the single point where a request
// actually leaves: `fetcher/refetch.js`.
//
// The decision is read from the URL parameters rather than the redux currency on
// purpose: DCC rewrites `currency.current` mid-session, and an in-flight checkout
// must not migrate to a different API host part-way through.

function __origin__(url) {
  try {
    return new URL(String(url)).origin;
  } catch (e) {
    return null;
  }
}

const DEFAULT_API_ORIGIN = __origin__(process.env.API_BASE_URL);
const WHITE_LABEL_API_ORIGIN = __origin__(process.env.WHITE_LABEL_API_BASE_URL);

const IS_WHITE_LABELLED = (function __resolve__() {
  try {
    return isWhiteLabelled(SearchParams.get('currency'), SearchParams.get('country'));
  } catch (e) {
    return false;
  }
})();

/**
 * Returns `resource` unchanged unless this is a white-labelled session AND the
 * request is bound for the branded API host. Everything else — the event log, the
 * Forter snippet, relative URLs — is left alone.
 *
 * Falls back to the branded host when the white-label origin is unconfigured or
 * unparseable: a branded domain in a network trace is a compliance problem, but a
 * checkout that cannot reach any API at all is a worse one.
 */
export default function withWhiteLabelledHost(resource) {
  if (
    IS_WHITE_LABELLED !== true ||
    DEFAULT_API_ORIGIN === null ||
    WHITE_LABEL_API_ORIGIN === null
  ) return resource;

  try {
    // `resource` may be a string, a URL, or a Request.
    const $url = new URL(String(resource && resource.url || resource));

    if ($url.origin !== DEFAULT_API_ORIGIN) return resource;

    // Swapping `host` + `protocol` preserves pathname, search and hash.
    const $target = new URL(WHITE_LABEL_API_ORIGIN);
    $url.protocol = $target.protocol;
    $url.host = $target.host;

    return $url.href;
  } catch (e) {
    return resource;
  }
}
